import {
  Badge,
  Box,
  Button,
  Flex,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  approvalColor,
  approvalLabel,
  formatDollars,
  formatFullDate,
  formatJsDate,
  formatPeriodShort,
  statusColor,
} from '~/backup/shared/format';
import {
  awaitingPeriodColor,
  awaitingPeriodLabel,
  currentPeriod,
  formatInputStatuses,
  hasFailedInput,
  todayFullDate,
} from '~/backup/shared/servicingPeriods';
import {
  PortalBusServicingPeriodsQueryGQLType,
  usePortalBusServicingCycleLazyQueryGQLType,
  usePortalBusServicingPeriodsQuery,
} from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';

type DealPeriod = PortalBusServicingPeriodsQueryGQLType['portalBusServicingPeriods'][number];
type DealCycle = NonNullable<DealPeriod['cycle']>;

/**
 * Downloads the certificate PDF for a single cycle. The signed URL is not on the
 * list row (it is minted per request), so we lazily fetch the cycle detail on
 * click and open the URL — or toast when it has not rendered yet. Tape / MSR
 * downloads are deferred (no signed-URL surface for those artifact refs yet).
 */
function CertDownloadButton({
  servicingCycleId,
}: {
  servicingCycleId: string;
}): React.ReactElement {
  const toast = useToast();
  const [fetchCycle, { loading }] = usePortalBusServicingCycleLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const url = data.portalBusServicingCycle?.certificateDownloadUrl ?? null;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast({
          title: 'Certificate not ready',
          description: 'The certificate PDF has not finished rendering yet.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    onError: () => {
      toast({
        title: 'Could not fetch certificate',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  return (
    <Button
      size="xs"
      variant="link"
      colorScheme="blue"
      isLoading={loading}
      onClick={() => fetchCycle({ variables: { servicingCycleId } })}
    >
      Cert PDF
    </Button>
  );
}

function ValidationCell({
  status,
}: {
  status: string | null | undefined;
}): React.ReactElement {
  const label = (status ?? 'pending').toUpperCase();
  return (
    <Text fontWeight={600} fontSize="0.8rem" color={statusColor(status ?? '')}>
      {label}
    </Text>
  );
}

/**
 * The servicer's accept/reject decision on the cycle. Its own column because
 * validation (the automated tie-out) and review (the human decision) are
 * different gates that can disagree — a cycle can tie out and still be returned.
 */
function ReviewCell({
  approvalStatus,
}: {
  approvalStatus: string;
}): React.ReactElement {
  return (
    <Text
      fontWeight={600}
      fontSize="0.8rem"
      color={approvalColor(approvalStatus)}
    >
      {approvalLabel(approvalStatus).toUpperCase()}
    </Text>
  );
}

/** The columns after Period for a reconciled period: the cycle's own state. */
function CycleCells({
  period,
  cycle,
}: {
  period: DealPeriod;
  cycle: DealCycle;
}): React.ReactElement {
  return (
    <>
      <Td>
        <Text fontSize="0.8rem">{formatJsDate(cycle.tapeReceivedAt)}</Text>
        <Text fontSize="0.75rem" color={colors.gray[500]}>
          {(cycle.tapeRowCount ?? 0).toLocaleString()} rows ·{' '}
          {formatDollars(cycle.tapeTotalPrincipal)}
        </Text>
        {/* The inputs follow the latest upload, the cycle the one it reconciled:
            a failed re-send leaves the cycle as it was, so say so. */}
        {hasFailedInput(period) && (
          <Text fontSize="0.75rem" color={colors.red[400]}>
            Latest upload failed
          </Text>
        )}
      </Td>
      <Td>
        <ValidationCell status={cycle.reconcileStatus} />
      </Td>
      <Td>
        <ReviewCell approvalStatus={cycle.approvalStatus} />
      </Td>
      <Td>
        {/* A rejected cycle never acquires a `certificateStatus`, so spell out
            that none is coming rather than showing a dash. */}
        <Text
          fontSize="0.8rem"
          color={
            !cycle.certificateStatus && cycle.approvalStatus === 'rejected'
              ? approvalColor(cycle.approvalStatus)
              : undefined
          }
        >
          {cycle.certificateStatus?.toUpperCase() ??
            (cycle.approvalStatus === 'rejected' ? 'NOT ISSUED' : '—')}
        </Text>
        {cycle.certificateIssuedAt !== null && (
          <Text fontSize="0.75rem" color={colors.gray[500]}>
            Issued {formatJsDate(cycle.certificateIssuedAt)}
          </Text>
        )}
      </Td>
      <Td>
        <Text fontSize="0.8rem" color={colors.gray[500]}>
          {formatJsDate(cycle.msrReceivedAt)}
        </Text>
      </Td>
      <Td>
        {cycle.certificateStatus ? (
          <CertDownloadButton servicingCycleId={cycle.id} />
        ) : (
          <Text fontSize="0.8rem" color={colors.gray[400]}>
            —
          </Text>
        )}
      </Td>
    </>
  );
}

/**
 * The columns after Period for a period with no cycle yet: nothing has
 * reconciled, so one cell says what it waits on and where each input stands.
 */
function AwaitingCells({
  period,
  today,
}: {
  period: DealPeriod;
  today: string;
}): React.ReactElement {
  return (
    <Td colSpan={6}>
      <Text
        fontWeight={600}
        fontSize="0.8rem"
        color={awaitingPeriodColor(period, today)}
      >
        {awaitingPeriodLabel(period, today)}
      </Text>
      <Text fontSize="0.75rem" color={colors.gray[500]}>
        Due {formatFullDate(period.dueDate)} ·{' '}
        {formatInputStatuses(period.inputs)}
      </Text>
    </Td>
  );
}

// -- component --

export const DeliverablesView = (): React.ReactElement => {
  const { implementationId } = useParams<{ implementationId: string }>();

  const { data, loading, error } = usePortalBusServicingPeriodsQuery({
    variables: { implementationId: implementationId ?? '' },
    skip: !implementationId,
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Flex justify="center" align="center" minH="20rem">
        <Spinner />
      </Flex>
    );
  }

  const periods: DealPeriod[] = data?.portalBusServicingPeriods ?? [];

  if (error) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          Something went wrong
        </Typography>
        <Text color={colors.gray[500]}>
          Could not load deliverables. Please try again or contact support.
        </Text>
      </Flex>
    );
  }

  if (periods.length === 0) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          No deliverables
        </Typography>
        <Text color={colors.gray[500]}>
          This deal has no scheduled servicing periods on your account.
        </Text>
      </Flex>
    );
  }

  const today = todayFullDate();
  const current = currentPeriod(periods, today);
  // Certified = the certificate has been issued. BUS certificates are pre-signed,
  // so an issued certificate is a signed certificate — there is no later signing
  // step to wait on.
  const certifiedCount = periods.filter(
    (p) => p.cycle?.certificateStatus === 'issued',
  ).length;

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="1.5rem"
    >
      {/* Header */}
      <Box>
        <Flex align="center" gap="0.5rem" mb="0.25rem">
          <Link
            as={RouterLink}
            to={`/deals/${implementationId ?? ''}`}
            fontSize="0.8rem"
            color={colors.gray[500]}
          >
            {periods[0].creditFacilityName}
          </Link>
          <Text color={colors.gray[300]} fontSize="0.8rem">
            /
          </Text>
          <Text fontSize="0.8rem" color={colors.gray[500]}>
            Deliverables
          </Text>
        </Flex>
        <Typography variant="page-title" trim>
          Monthly Deliverables
        </Typography>
        <Text color={colors.gray[500]} fontSize="0.9rem">
          {certifiedCount} of {periods.length} period
          {periods.length === 1 ? '' : 's'} certified
        </Text>
      </Box>

      {/* Table */}
      <TableContainer
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="md"
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Period</Th>
              <Th>Tape</Th>
              <Th>Validation</Th>
              <Th>Review</Th>
              <Th>Certificate</Th>
              <Th>MSR</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {periods.map((p) => (
              <Tr key={p.reportingPeriod} _hover={{ bg: colors.gray[50] }}>
                <Td fontWeight={600}>
                  <Flex align="center" gap="0.4rem">
                    {/* Drills into the period-scoped Overview. A real link (not a
                        row onClick) so it is keyboard-reachable and the Cert PDF
                        button in the last column stays unambiguous. */}
                    <Link
                      as={RouterLink}
                      to={`/deals/${implementationId ?? ''}/periods/${
                        p.reportingPeriod
                      }`}
                      color={colors.blue[400]}
                    >
                      {formatPeriodShort(p.reportingPeriod)}
                    </Link>
                    {p === current && (
                      <Badge colorScheme="blue" fontSize="0.6rem">
                        Current
                      </Badge>
                    )}
                  </Flex>
                </Td>
                {p.cycle ? (
                  <CycleCells period={p} cycle={p.cycle} />
                ) : (
                  <AwaitingCells period={p} today={today} />
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};
